import { ERROR_MESSAGES, type ApiErrorBody, type ErrorCode } from '@yana/shared';
import { beginApiActivity } from './activity';

/**
 * HTTP-клиент.
 *
 * Все запросы идут на тот же origin (в dev — через прокси Vite), поэтому
 * cookie сессии уезжает сама и возни с CORS и SameSite не возникает.
 */

const BASE = '/api/v1';

/** Ошибка, о которой можно осмысленно сообщить пользователю. */
export class ApiError extends Error {
  readonly code: ErrorCode | string;
  readonly status: number;
  readonly fields: Record<string, string> | undefined;
  readonly details: Record<string, unknown> | undefined;
  readonly requestId: string | undefined;

  constructor(status: number, body: ApiErrorBody['error']) {
    super(body.message || ERROR_MESSAGES.INTERNAL_ERROR);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.fields = body.fields;
    this.details = body.details;
    this.requestId = body.requestId;
  }

  get isConflict(): boolean {
    return this.status === 409;
  }
  get isForbidden(): boolean {
    return this.status === 403;
  }
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

/** Вызывается при 401 — роутер уводит на экран входа. */
let onUnauthenticated: (() => void) | null = null;
export function setUnauthenticatedHandler(fn: () => void): void {
  onUnauthenticated = fn;
}

/** HTTP и WebSocket используют один переход на экран входа. */
export function notifyUnauthenticated(): void {
  onUnauthenticated?.();
}

function errorFromResponse(body: unknown, status: number): ApiErrorBody['error'] {
  const error = body && typeof body === 'object' && 'error' in body ? body.error : null;
  if (error && typeof error === 'object' && 'code' in error && typeof error.code === 'string'
    && 'message' in error && typeof error.message === 'string' && error.message.trim()) {
    return error as ApiErrorBody['error'];
  }

  // Прокси возвращает HTML или служебный текст. Показывать их человеку нельзя.
  const message = status === 502 || status === 503
    ? 'Сервер временно недоступен. Повторите действие через несколько секунд.'
    : status === 504
      ? 'Сервер не успел ответить. Повторите действие через несколько секунд.'
      : ERROR_MESSAGES.INTERNAL_ERROR;
  return { code: 'INTERNAL_ERROR', message };
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const completed = beginApiActivity(path, init);
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
      ...(init.headers ?? {}),
    },
  });

  if (res.status === 204) { completed(undefined); return undefined as T; }

  const text = await res.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }

  if (!res.ok) {
    const errBody = errorFromResponse(body, res.status);

    if (res.status === 401) notifyUnauthenticated();

    throw new ApiError(res.status, errBody);
  }

  completed(body);
  return body as T;
}

export const api = {
  get: <T>(path: string): Promise<T> => request<T>(path),

  post: <T>(path: string, data?: unknown): Promise<T> =>
    request<T>(path, { method: 'POST', body: data === undefined ? undefined : JSON.stringify(data) }),

  patch: <T>(path: string, data?: unknown): Promise<T> =>
    request<T>(path, {
      method: 'PATCH',
      body: data === undefined ? undefined : JSON.stringify(data),
    }),

  put: <T>(path: string, data?: unknown): Promise<T> =>
    request<T>(path, { method: 'PUT', body: data === undefined ? undefined : JSON.stringify(data) }),

  delete: <T>(path: string): Promise<T> => request<T>(path, { method: 'DELETE' }),
};

/** Идентификатор клиентской операции: идемпотентность + узнавание своего эха в realtime. */
const localOperations = new Set<string>();

export function isOwnOperation(id: string | null | undefined): boolean {
  return !!id && localOperations.has(id);
}

export function newOpId(): string {
  const id = crypto.randomUUID();
  localOperations.add(id);
  if (localOperations.size > 2000) localOperations.delete(localOperations.values().next().value!);
  return id;
}
